import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, AlertCircle, Loader, ShieldCheck } from 'lucide-react';
import { formatDoctorName } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyPrescription() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/prescriptions/verify/${token}`);
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [token]);

  // Loader
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <Loader size={48} style={{ color: '#0596DE', animation: 'spin 1s linear infinite' }} />
          <p style={styles.loadingText}>Vérification en cours...</p>
        </div>
      </div>
    );
  }

  // Erreur réseau
  if (error || !data) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <AlertCircle size={64} color="#f59e0b" />
          <h1 style={styles.title}>Erreur de connexion</h1>
          <p style={styles.subtitle}>Impossible de vérifier cette ordonnance pour le moment.</p>
          <p style={styles.subtitle}>Veuillez réessayer plus tard.</p>
        </div>
      </div>
    );
  }

  // Ordonnance valide
  if (data.valid === true) {
    const dateIssued = data.issued_at ? new Date(data.issued_at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '';
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, borderTop: '6px solid #16a34a' }}>
          <CheckCircle size={64} color="#16a34a" />
          <h1 style={{ ...styles.title, color: '#16a34a' }}>Ordonnance authentique</h1>
          <p style={styles.subtitle}>Ce certificat a bien été émis par Tabbibak.dz</p>

          <div style={styles.detailsBox}>
            <div style={styles.detailRow}>
              <span style={styles.label}>N° d'ordonnance</span>
              <span style={styles.value}>{data.token}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Médecin émetteur</span>
              <span style={styles.value}>{formatDoctorName(data.doctor_name)}</span>
            </div>
            {data.doctor_specialty && (
              <div style={styles.detailRow}>
                <span style={styles.label}>Spécialité</span>
                <span style={styles.value}>{data.doctor_specialty}</span>
              </div>
            )}
            <div style={styles.detailRow}>
              <span style={styles.label}>Date d'émission</span>
              <span style={styles.value}>{dateIssued}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Médicaments prescrits</span>
              <span style={styles.value}>{data.drugs_count}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Statut</span>
              <span style={{ ...styles.value, color: '#16a34a', fontWeight: 'bold' }}>✓ Valide</span>
            </div>
          </div>

          <div style={styles.footer}>
            <ShieldCheck size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Vérifié par Tabbibak.dz
          </div>
        </div>
      </div>
    );
  }

  // Ordonnance révoquée
  if (data.reason === 'revoked') {
    const revokedDate = data.revoked_at ? new Date(data.revoked_at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '';
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, borderTop: '6px solid #dc2626' }}>
          <XCircle size={64} color="#dc2626" />
          <h1 style={{ ...styles.title, color: '#dc2626' }}>Ordonnance révoquée</h1>
          <p style={styles.subtitle}>Cette ordonnance a été annulée par le médecin.</p>
          <p style={{ ...styles.subtitle, fontWeight: 'bold', color: '#dc2626' }}>Ne pas délivrer.</p>

          <div style={styles.detailsBox}>
            <div style={styles.detailRow}>
              <span style={styles.label}>N° d'ordonnance</span>
              <span style={styles.value}>{data.token}</span>
            </div>
            {revokedDate && (
              <div style={styles.detailRow}>
                <span style={styles.label}>Annulée le</span>
                <span style={styles.value}>{revokedDate}</span>
              </div>
            )}
          </div>

          <div style={styles.footer}>
            <ShieldCheck size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Vérifié par Tabbibak.dz
          </div>
        </div>
      </div>
    );
  }

  // Token introuvable
  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, borderTop: '6px solid #f59e0b' }}>
        <AlertCircle size={64} color="#f59e0b" />
        <h1 style={{ ...styles.title, color: '#f59e0b' }}>Ordonnance introuvable</h1>
        <p style={styles.subtitle}>Ce numéro d'ordonnance n'existe pas dans nos registres.</p>
        <p style={styles.subtitle}>Si vous avez un doute, contactez le médecin émetteur.</p>

        <div style={styles.footer}>
          <ShieldCheck size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Vérifié par Tabbibak.dz
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '40px 32px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  title: {
    fontSize: '24px',
    margin: '16px 0 8px',
    color: '#0596DE',
  },
  subtitle: {
    fontSize: '15px',
    color: '#64748b',
    margin: '8px 0',
    lineHeight: 1.5,
  },
  loadingText: {
    fontSize: '16px',
    color: '#64748b',
    marginTop: '16px',
  },
  detailsBox: {
    background: '#f8fafc',
    borderRadius: '12px',
    padding: '20px',
    margin: '24px 0',
    textAlign: 'left',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '14px',
  },
  label: {
    color: '#64748b',
    fontWeight: 500,
  },
  value: {
    color: '#1a1a1a',
    fontWeight: 600,
    fontFamily: 'monospace',
  },
  footer: {
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
    color: '#94a3b8',
    fontSize: '13px',
  },
};
